/**
 * Framework-free OIDC/PKCE helpers for the ForgeRock Angular auth provider (Task 28; spec docs/superpowers/specs/2026-09-22-forgerock-activation-design.md).
 *
 * Every export here is a pure function or a plain data shape — no Angular, no DOM writes, no
 * network calls — so they are unit-testable in isolation (see forgerock-oidc.spec.ts) without a
 * live ForgeRock tenant or Angular's TestBed. The stateful half of the OIDC exchange (discovery
 * caching, sessionStorage reads/writes, the actual code exchange via `oauth4webapi`) lives in
 * forgerock-provider.service.ts, which imports and calls these helpers.
 */
import type { AuthorizationServer } from 'oauth4webapi';
import type { StandardUserInfo } from '@memberjunction/ng-auth-services';

/**
 * The five FORGEROCK_* environment keys this provider reads. Set (with AUTH_TYPE 'forgerock') on
 * stage and in dev since 2026-09-22; '' in any environment the switch-over runbook has not yet
 * reached, where this provider stays dormant.
 */
export interface ForgeRockEnvironmentConfig {
  readonly FORGEROCK_ISSUER: string;
  readonly FORGEROCK_CLIENTID: string;
  readonly FORGEROCK_REDIRECT_URI: string;
  readonly FORGEROCK_SCOPES: string;
  /**
   * Name of the ForgeRock AM authentication journey (tree) to run, sent as the `service`
   * query parameter on the authorize request. '' (default) sends none and AM runs the realm's
   * default journey. AGU's realm-default journey rejects every member login ("Username or
   * Password not Recognized" / 401 "Login failure", probed 2026-09-22); agu.org itself sends
   * `service=AuthAGU`, and that journey authenticates the same accounts. Set to `AuthAGU`.
   */
  readonly FORGEROCK_SERVICE: string;
}

/** PKCE + replay-protection parameters generated for one authorization attempt. */
export interface PkceAttempt {
  readonly codeVerifier: string;
  readonly codeChallenge: string;
  readonly state: string;
  readonly nonce: string;
}

/**
 * True only for an issuer URL that is both `http:` (not `https:`) and points at `localhost` or
 * `127.0.0.1`. `oauth4webapi@3.8.6` enforces HTTPS on every network call
 * (`node_modules/oauth4webapi/build/index.js`'s `checkProtocol`) and throws
 * `OAUTH_HTTP_REQUEST_FORBIDDEN` otherwise — this predicate is the single gate the provider uses
 * to decide when to pass `[oauth.allowInsecureRequests]: true` to `discoveryRequest`,
 * `authorizationCodeGrantRequest`, and `refreshTokenGrantRequest` (forgerock-provider.service.ts).
 * It exists so the Explorer rehearsal against `tools/forgerock-mock/` (plain `http://localhost:8090`,
 * no TLS) works at all, while any non-local issuer — stage, prod, or a real ForgeRock tenant
 * reached over `http://` by mistake — is still forced onto HTTPS. Returns `false` (never throws)
 * for a malformed issuer URL; the caller's own `new URL(...)` construction surfaces that error.
 */
export function IsLocalInsecureIssuer(issuer: string): boolean {
  let url: URL;
  try {
    url = new URL(issuer);
  } catch {
    return false;
  }
  return url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1');
}

/**
 * True only when the three keys required to attempt an authorization request are non-empty.
 * When `environment.AUTH_TYPE` is not 'forgerock', `AuthServicesModule.forRoot` never instantiates
 * this provider (stage/dev have been 'forgerock' since 2026-09-22); this is the second gate, so
 * that even a caller that reaches this class directly gets a clear refusal instead of a deep
 * failure inside `oauth4webapi` against an empty issuer URL.
 */
export function IsForgeRockConfigured(config: Partial<ForgeRockEnvironmentConfig>): boolean {
  return !!config.FORGEROCK_ISSUER && !!config.FORGEROCK_CLIENTID && !!config.FORGEROCK_REDIRECT_URI;
}

/**
 * Builds the Authorization Code + PKCE request URL against the discovered `authorization_endpoint`.
 * `oauth4webapi` is deliberately low-level and does not build this URL for the caller — composing
 * it from discovery metadata + client + PKCE/state/nonce is the library's documented pattern
 * (see its `examples/oidc.ts`).
 */
export function BuildForgeRockAuthorizationUrl(
  server: Pick<AuthorizationServer, 'authorization_endpoint'>,
  clientID: string,
  redirectUri: string,
  scopes: string,
  pkce: PkceAttempt,
  service = ''
): URL {
  if (!server.authorization_endpoint) {
    throw new Error('ForgeRock discovery document is missing authorization_endpoint');
  }
  const url = new URL(server.authorization_endpoint);
  url.searchParams.set('client_id', clientID);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', scopes);
  url.searchParams.set('state', pkce.state);
  url.searchParams.set('nonce', pkce.nonce);
  url.searchParams.set('code_challenge', pkce.codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  // AM-specific: selects the authentication journey (see ForgeRockEnvironmentConfig.FORGEROCK_SERVICE).
  const journey = service.trim();
  if (journey.length > 0) {
    url.searchParams.set('service', journey);
  }
  return url;
}

/**
 * Decoded claims this provider surfaces from a ForgeRock ID token payload. `customerKey` is the
 * NetForum customer key AGU places in `cst_key` (and also in `sub`); MJAPI's ForgeRockProvider
 * reads the same claim independently off the verified token (spec F2 in
 * docs/superpowers/specs/2026-09-22-forgerock-activation-design.md). Kept here so the mapping
 * is unit-tested; never used for a security decision.
 */
export interface ForgeRockIdTokenClaims {
  readonly sub: string;
  readonly email?: string;
  readonly emailVerified?: boolean;
  readonly givenName?: string;
  readonly familyName?: string;
  readonly name?: string;
  readonly preferredUsername?: string;
  readonly picture?: string;
  readonly locale?: string;
  readonly customerKey?: string;
}

function stringClaim(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

/**
 * Maps a raw ID token JSON payload to the fields this provider cares about. `payload` is
 * `Record<string, unknown>` because it is untrusted JSON off the wire (dev-best-practices §2
 * boundary rule: use `Record<string, unknown>` and narrow with `typeof` guards) — every field is
 * narrowed before use. Decoding here is never used for a security decision; see
 * `DecodeUnverifiedJwtPayload` below.
 */
export function ExtractForgeRockClaims(payload: Record<string, unknown>): ForgeRockIdTokenClaims {
  const sub = stringClaim(payload, 'sub');
  if (!sub) {
    throw new Error('ForgeRock ID token is missing the required sub claim');
  }
  const emailVerifiedRaw = payload['email_verified'];
  return {
    sub,
    email: stringClaim(payload, 'email'),
    emailVerified: typeof emailVerifiedRaw === 'boolean' ? emailVerifiedRaw : undefined,
    givenName: stringClaim(payload, 'given_name'),
    familyName: stringClaim(payload, 'family_name'),
    name: stringClaim(payload, 'name'),
    preferredUsername: stringClaim(payload, 'preferred_username'),
    picture: stringClaim(payload, 'picture'),
    locale: stringClaim(payload, 'locale'),
    customerKey: stringClaim(payload, 'cst_key') ?? sub,
  };
}

/** Maps decoded ForgeRock claims onto the standard shape every MJExplorer auth provider returns. */
export function MapForgeRockClaimsToStandardUserInfo(claims: ForgeRockIdTokenClaims): StandardUserInfo {
  const name = claims.name || [claims.givenName, claims.familyName].filter(Boolean).join(' ') || claims.email || claims.sub;
  return {
    id: claims.sub,
    email: claims.email || '',
    name,
    givenName: claims.givenName,
    familyName: claims.familyName,
    preferredUsername: claims.preferredUsername || claims.email,
    pictureUrl: claims.picture,
    locale: claims.locale,
    emailVerified: claims.emailVerified,
  };
}

/**
 * Decodes (without verifying) the payload segment of a JWT, for UI-facing claim display during
 * silent session restore (`initialize()` in forgerock-provider.service.ts) when only the cached
 * raw ID token string — not a live `oauth4webapi` token response — is available. This is never
 * used for a security decision: MJAPI independently validates the signature, issuer, audience,
 * and expiry of every ID token it receives (`BaseAuthProvider` + JWKS) on each GraphQL request.
 */
export function DecodeUnverifiedJwtPayload(jwt: string): Record<string, unknown> {
  const parts = jwt.split('.');
  if (parts.length !== 3) {
    throw new Error('Not a JWT (expected three dot-separated segments)');
  }
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  const utf8 = new TextDecoder().decode(bytes);
  const parsed: unknown = JSON.parse(utf8);
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('JWT payload did not decode to a JSON object');
  }
  return parsed as Record<string, unknown>;
}

/**
 * True when the current URL's query string carries an OAuth authorization-code callback — either
 * a `code` (success) or an `error` from the IdP. `MJForgeRockProvider.initialize()` uses this to
 * decide whether it must finish the redirect exchange before it reports auth state, mirroring the
 * Okta provider's `isLoginRedirect()` gate. Without that gate the first login lands the app on
 * `?code=…` with no token in sessionStorage yet, `initialize()` reports "not authenticated", and
 * the Explorer bootstrap stalls on "Loading workspace…" until a manual reload.
 */
export function UrlHasAuthCallbackParams(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has('code') || params.has('error');
}

/**
 * A return-to path is only ever a same-origin, app-relative path: one leading `/`, not the root,
 * no protocol-relative `//host`, no backslash tricks, and never a URL that itself carries OAuth
 * callback params (restoring one would replay a spent authorization code). Spec H4.
 */
export function IsSafeReturnPath(path: string): boolean {
  if (path.length < 2 || !path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
    return false;
  }
  if (/[\u0000-\u001F\u007F]/.test(path)) {
    // The WHATWG URL parser *strips* C0 control characters before resolving, so a shape-only check
    // would accept `/<tab>/evil.example` and the browser would then resolve it protocol-relative
    // (cross-origin). Reject them outright rather than reason about what survives parsing.
    return false;
  }
  const queryStart = path.indexOf('?');
  const search = queryStart === -1 ? '' : path.slice(queryStart);
  return !UrlHasAuthCallbackParams(search);
}

/**
 * Where to send the user after the redirect login completes. MJ's convention is
 * `login({ appState: { target: '/path' } })` (ng-auth-services `IAuthProvider.d.ts`, used by the
 * shell's `handleAuthRetry`); otherwise the page the login started from. `null` when neither is a
 * safe path (e.g. login from the root — nothing to restore). Spec H4.
 */
export function ResolveReturnTo(options: Record<string, unknown> | undefined, currentPath: string): string | null {
  const appState = options?.['appState'];
  if (appState !== null && typeof appState === 'object') {
    const target = (appState as Record<string, unknown>)['target'];
    if (typeof target === 'string' && IsSafeReturnPath(target)) {
      return target;
    }
  }
  return IsSafeReturnPath(currentPath) ? currentPath : null;
}
